import { useMemo, forwardRef } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { DistanceToLayerResponse } from '../../../services/api';
import { CHART_COLORS } from '../../../utils/SolarChart.utils';
import { ChartEmptyState } from '../ChartEmptyState';

export interface DistributionBin {
  min: number;
  max: number;
  percentage: number;
}

interface AreaStats {
  min: number;
  max: number;
  mean: number;
  std_dev: number;
  annual_mean?: number;
}

interface Props {
  distribution: DistributionBin[];
  stats: AreaStats | null;
  unit: string;
  barColor?: string;
  distanceData?: {
    existing?: DistanceToLayerResponse;
    substation_existing?: DistanceToLayerResponse;
  } | null;
  systemMode?: "sin" | "distribuidora";
}

function statsGridCols(hasLine: unknown, hasSub: unknown): string {
  if (hasLine && hasSub) return 'grid-cols-6';
  if (hasLine || hasSub) return 'grid-cols-5';
  return 'grid-cols-4';
}

export const DistributionChart = forwardRef<HTMLDivElement, Props>(
  ({ distribution, stats, unit, barColor, distanceData, systemMode }, ref) => {
    const { categories, data } = useMemo(() => {
      if (!distribution || distribution.length === 0) return { categories: [], data: [] };
      const reversed = [...distribution].reverse();
      const cats = reversed.map((bin, i) => {
        const original = distribution.length - 1 - i;
        if (original === 0) return `> ${bin.min.toFixed(2)}`;
        if (original === distribution.length - 1) return `< ${bin.max.toFixed(2)}`;
        return `${bin.max.toFixed(2)} – ${bin.min.toFixed(2)}`;
      });
      const vals = reversed.map(bin => parseFloat(bin.percentage.toFixed(1)));
      return { categories: cats, data: vals };
    }, [distribution]);

    const root = typeof window !== 'undefined' ? getComputedStyle(document.documentElement) : null;
    const textSecondary = root?.getPropertyValue('--color-gray-light').trim() || '#61656d';
    const gridLineColor = root?.getPropertyValue('--color-gray-pale').trim() || '#d9d9d9';
    const axisLineColor = root?.getPropertyValue('--border').trim() || '#d9d9d9';
    const backgroundColor = root?.getPropertyValue('--background').trim() || '#ffffff';
    const borderColor = root?.getPropertyValue('--border').trim() || '#d9d9d9';
    const textPrimary = root?.getPropertyValue('--foreground').trim() || '#000000';

    const nearestDistance = useMemo(() => {
      if (!distanceData) return null;
      const existingDist = distanceData.existing?.nearest_feature.distance_km;
      if (existingDist !== undefined) {
        const props = distanceData.existing?.nearest_feature.properties ?? {};
        const lineName = systemMode === "distribuidora"
          ? (props.nome || props.nomelongo || 'Linha de Distribuição')
          : (props.nomelongo || props.nome || 'LT Existente');
        const label = systemMode === "distribuidora" ? "Dist LD" : "Dist LT";
        return { distanceKm: existingDist, lineName, label };
      }
      return null;
    }, [distanceData, systemMode]);

    const nearestSubstation = useMemo(() => {
      const sub = distanceData?.substation_existing?.nearest_feature;
      if (!sub || typeof sub.distance_km !== 'number') return null;
      const props = sub.properties ?? {};
      const name = props.cod_id || props.nome || props.nomelongo ||
        (systemMode === "distribuidora" ? "Unidade de Transformação" : "Subestação");
      const label = systemMode === "distribuidora" ? "Dist UT" : "Dist SE";
      return { distanceKm: sub.distance_km, name, label };
    }, [distanceData, systemMode]);

    const options: Highcharts.Options = useMemo(() => ({
      chart: {
        type: 'bar',
        backgroundColor: 'transparent',
        height: Math.max(180, (distribution?.length ?? 0) * 44 + 60),
        marginTop: 10,
        spacingBottom: 5,
      },
      title: { text: undefined },
      legend: { enabled: false },
      credits: { enabled: false },
      exporting: { enabled: false },
      xAxis: {
        categories,
        labels: { style: { fontSize: '11px', color: textSecondary } },
        lineColor: axisLineColor,
        tickColor: 'transparent',
        title: { text: unit, style: { fontSize: '11px', color: textSecondary, fontWeight: 'normal' } },
      },
      yAxis: {
        min: 0,
        title: { text: '%', style: { fontSize: '11px', color: textSecondary, fontWeight: 'normal' } },
        labels: { format: '{value}%', style: { fontSize: '11px', color: textSecondary } },
        gridLineColor,
      },
      tooltip: {
        backgroundColor, borderColor, borderRadius: 10, padding: 10, shadow: true, useHTML: true,
        headerFormat: `<div style="font-size:11px;color:${textSecondary};margin-bottom:6px;">{point.key}</div>`,
        pointFormat: `<div style="font-weight:bold;color:${textPrimary};font-size:12px;"><span style="color:${CHART_COLORS.MEAN};">●</span> {point.y:.1f}%</div>`,
      },
      plotOptions: {
        bar: {
          borderWidth: 0,
          borderRadius: 10,
          pointWidth: 16,
          dataLabels: {
            enabled: true,
            format: '{point.y:.1f}%',
            style: { fontSize: '11px', fontWeight: 'bold', color: textSecondary, textOutline: 'none' },
          },
        },
      },
      series: [{
        type: 'bar',
        name: `Distribuição (${unit})`,
        data: data.map(v => ({ y: v, color: barColor ?? CHART_COLORS.MEAN })),
      }],
    }), [categories, data, unit, textSecondary, axisLineColor, gridLineColor, backgroundColor, borderColor, textPrimary, distribution]);

    if (!distribution || distribution.length === 0) {
      return (
        <ChartEmptyState
          ref={ref}
          message="Sem dados disponíveis para esta seleção."
        />
      );
    }

    return (
      <div ref={ref} className="flex flex-col relative pb-4 bg-white rounded-lg shadow-md">
        <div className="w-full">
          <HighchartsReact highcharts={Highcharts} options={options} />
        </div>

        {stats && (
          <div className={`grid gap-2 mt-3 pt-3 border-t border-gray-100 text-center ${statsGridCols(nearestDistance, nearestSubstation)}`}>
            <StatItem label="Mín" value={`${stats.min.toFixed(2)} ${unit}`} />
            <StatItem label="Máx" value={`${stats.max.toFixed(2)} ${unit}`} />
            <StatItem label="Média" value={`${stats.mean.toFixed(2)} ${unit}`} />
            <StatItem label="Desvio" value={stats.std_dev.toFixed(2)} />
            {nearestDistance && (
              <StatItem
                label={nearestDistance.label}
                value={`${nearestDistance.distanceKm.toFixed(2)} km`}
                tooltip={nearestDistance.lineName}
              />
            )}
            {nearestSubstation && (
              <StatItem
                label={nearestSubstation.label}
                value={`${nearestSubstation.distanceKm.toFixed(2)} km`}
                tooltip={nearestSubstation.name}
              />
            )}
          </div>
        )}
      </div>
    );
  }
);

DistributionChart.displayName = 'DistributionChart';

const StatItem = ({ label, value, tooltip }: { label: string; value: string; tooltip?: string }) => (
  <div className="flex flex-col items-center" title={tooltip}>
    <span className="text-[11px] text-gray-400 uppercase mb-1">{label}</span>
    <span className="text-[13px] font-semibold text-gray-800">{value}</span>
  </div>
);
